import React, { useEffect, useMemo, useState } from 'react';
import { Loader2, Save, RotateCcw, Tag } from 'lucide-react';
import { apiClient } from '../../services/api';
import { toast } from '../../hooks/use-toast';

/**
 * Nav labels editor — super_admin can rename any sidebar tab or group.
 * Reads the NAV registry via /api/nav-labels, presents each item with an
 * editable label input, and PUTs the full map on Save. Leaving a label
 * blank resets it to the built-in default.
 */

// Kept in sync with `NAV` in AdminLayout.jsx. If a new tab is added there,
// simply extend this list — nothing else to configure.
const DEFAULT_ITEMS = [
  { key: '/admin', default: 'Overview', group: null },
  { key: 'group:CRM', default: 'CRM', group: '__group__' },
  { key: '/admin/my-day', default: 'My Day', group: 'CRM' },
  { key: '/admin/leads', default: 'Leads', group: 'CRM' },
  { key: '/admin/crm', default: 'Tickets', group: 'CRM' },
  { key: '/admin/calendar', default: 'Calendar', group: 'CRM' },
  { key: '/admin/workload', default: 'Workload', group: 'CRM' },
  { key: '/admin/broadcasts', default: 'Broadcasts', group: 'CRM' },
  { key: 'group:HR & Payroll', default: 'HR & Payroll', group: '__group__' },
  { key: '/admin/hr', default: 'HR', group: 'HR & Payroll' },
  { key: '/admin/team', default: 'My team', group: 'HR & Payroll' },
  { key: '/admin/recruitment', default: 'Recruitment', group: 'HR & Payroll' },
  { key: '/admin/onboarding', default: 'Onboarding', group: 'HR & Payroll' },
  { key: '/admin/letter-templates', default: 'Letter templates', group: 'HR & Payroll' },
  { key: '/admin/attendance', default: 'Attendance', group: 'HR & Payroll' },
  { key: '/admin/attendance-approvals', default: 'Attendance approvals', group: 'HR & Payroll' },
  { key: '/admin/performance', default: 'Performance', group: 'HR & Payroll' },
  { key: '/admin/payroll', default: 'Payroll', group: 'HR & Payroll' },
  { key: '/admin/payroll-status', default: 'Payroll status', group: 'HR & Payroll' },
  { key: '/admin/loans', default: 'Loans', group: 'HR & Payroll' },
  { key: '/admin/tax-slabs', default: 'Tax slabs', group: 'HR & Payroll' },
  { key: '/admin/statutory-reports', default: 'Statutory reports', group: 'HR & Payroll' },
  { key: '/admin/accounts-queue', default: 'Accounts inbox', group: 'HR & Payroll' },
  { key: 'group:Newsroom', default: 'Newsroom', group: '__group__' },
  { key: '/admin/news', default: 'Company News', group: 'Newsroom' },
  { key: '/admin/sayings', default: 'Daily Quotes', group: 'Newsroom' },
  { key: '/admin/galleries', default: 'Galleries', group: 'Newsroom' },
  { key: 'group:Site', default: 'Site', group: '__group__' },
  { key: '/admin/home', default: 'Homepage', group: 'Site' },
  { key: '/admin/about', default: 'About Us', group: 'Site' },
  { key: '/admin/people', default: 'Leadership & People', group: 'Site' },
  { key: '/admin/verticals', default: 'Business Verticals', group: 'Site' },
  { key: 'group:System', default: 'System', group: '__group__' },
  { key: '/admin/inbox', default: 'Inbox & Subscribers', group: 'System' },
  { key: '/admin/masters', default: 'Masters', group: 'System' },
  { key: '/admin/users', default: 'Users & Roles', group: 'System' },
  { key: '/admin/nav-labels', default: 'Rename Tabs', group: 'System' },
  { key: 'group:Integrations', default: 'Integrations', group: '__group__' },
  { key: '/admin/books-link', default: 'Emerald Books', group: 'Integrations' },
];

const NavLabelsPage = () => {
  const [overrides, setOverrides] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState('');

  useEffect(() => {
    apiClient.get('/nav-labels').then((r) => setOverrides(r.data?.data || {}))
      .finally(() => setLoading(false));
  }, []);

  const dirty = useMemo(() => DEFAULT_ITEMS.some((it) => {
    const v = (overrides[it.key] || '').trim();
    // dirty if non-empty override that isn't equal to default
    return v && v !== it.default;
  }), [overrides]);

  const setLabel = (key, value) => setOverrides((o) => ({ ...o, [key]: value }));
  const resetOne = (key) => {
    const copy = { ...overrides };
    delete copy[key];
    setOverrides(copy);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Only send changed keys (non-empty AND different from default)
      const changed = {};
      DEFAULT_ITEMS.forEach((it) => {
        const v = (overrides[it.key] || '').trim();
        if (v && v !== it.default) changed[it.key] = v;
        else if (!v) changed[it.key] = '';  // signals delete on backend
      });
      await apiClient.put('/nav-labels', { labels: changed });
      toast({ title: 'Sidebar labels saved', description: 'The sidebar now reflects your new names.' });
      window.dispatchEvent(new Event('nav-labels-updated'));
    } catch (e) {
      toast({ title: e?.response?.data?.detail || 'Save failed', variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const visible = DEFAULT_ITEMS.filter((it) =>
    !filter || it.default.toLowerCase().includes(filter.toLowerCase()) || (overrides[it.key] || '').toLowerCase().includes(filter.toLowerCase()));

  return (
    <div className="max-w-4xl" data-testid="nav-labels-page">
      <div className="mb-6">
        <span className="text-xs font-semibold tracking-[0.25em] text-[#0f5c3d] uppercase inline-flex items-center gap-1.5">
          <Tag className="w-3.5 h-3.5" /> Sidebar
        </span>
        <h1 className="mt-2 text-4xl text-[#0b1a2e]" style={{ fontFamily: 'Cormorant Garamond, serif', fontWeight: 500 }}>Rename Tabs</h1>
        <p className="mt-2 text-sm text-[#0b1a2e]/60">Rename any sidebar tab or group heading. Leave a field blank to reset to the default name.</p>
      </div>

      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Search…"
          data-testid="nav-labels-search"
          className="px-3 py-2 rounded-lg border border-slate-300 text-sm w-64" />
        <button onClick={save} disabled={saving} data-testid="nav-labels-save"
          className="inline-flex items-center gap-2 px-5 py-2 rounded-lg bg-[#0f5c3d] text-white text-sm font-semibold disabled:opacity-60 hover:bg-[#0b4a30]">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save all
        </button>
      </div>

      {loading ? (
        <div className="p-10 text-center text-[#0b1a2e]/50"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
      ) : (
        <div className="bg-white border border-black/5 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#f7fbf8] text-[10px] uppercase tracking-widest text-[#0b1a2e]/60">
              <tr>
                <th className="text-left p-3 w-1/3">Default name</th>
                <th className="text-left p-3 w-1/3">Your custom name</th>
                <th className="text-left p-3 w-1/4 text-[10px]">Location</th>
                <th className="p-3"></th>
              </tr>
            </thead>
            <tbody>
              {visible.map((it) => {
                const isGroup = it.group === '__group__';
                const current = overrides[it.key] || '';
                return (
                  <tr key={it.key} className={`border-t border-black/5 ${isGroup ? 'bg-[#f7fbf8]/50' : ''}`} data-testid={`nav-label-row-${it.key}`}>
                    <td className={`p-3 ${isGroup ? 'font-semibold text-[10px] uppercase tracking-widest text-[#0f5c3d]' : ''}`}>
                      {it.default}
                    </td>
                    <td className="p-3">
                      <input
                        value={current}
                        onChange={(e) => setLabel(it.key, e.target.value)}
                        placeholder={it.default}
                        data-testid={`nav-label-input-${it.key}`}
                        className="w-full px-2 py-1.5 rounded border border-slate-300 text-sm"
                      />
                    </td>
                    <td className="p-3 text-[10px] font-mono text-[#0b1a2e]/50">
                      {isGroup ? 'group' : (it.group || 'top')}
                    </td>
                    <td className="p-3 text-right">
                      {current && (
                        <button onClick={() => resetOne(it.key)} title="Reset to default"
                          className="text-[#0b1a2e]/50 hover:text-red-600">
                          <RotateCcw className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {dirty && (
        <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-2">
          You have unsaved changes. Click <b>Save all</b> to apply them to the sidebar.
        </div>
      )}
    </div>
  );
};

export default NavLabelsPage;
